import type { Card } from "@amzn/ads-blink-client";
import { InsightType } from "@amzn/d16-g-client-deck-builder-typescript-client";
import type { AsyncStatus } from "@react-hookz/web";
import { useDebouncedEffect } from "@react-hookz/web";
import { useCallback, useEffect, useRef, useState } from "react";
import * as uuid from "uuid";
import { useStore } from "zustand";

import { getDiscoveryApiClient } from "../../api/discoveryApi/discoveryApi";
import { getPublisherPortalApiClient } from "../../api/publisherPortalApi/publisherPortalApi";
import { ContentSearchErrorNotificationMessage } from "../../components/common/notification.components";
import { FeatureFlags, FeatureFlagsStore } from "../../store/featureFlags";
import { notificationStore, NotificationType } from "../../store/notification";
import { unifiedFilterStore } from "../../store/unifiedFilters";
import { ContentPreviewType } from "../../types/content";
import type { BaseFilterOptionModel } from "../../types/filters/common";
import {
    CoreUnifiedFilterKeys,
    UnifiedFilterKeys,
} from "../../types/filters/unified";
import { isLoading, isSuccess } from "../../utils/api.utils";
import { getPreselectedLanguages } from "../../utils/filters.utils";
import {
    getAdvertiserId,
    getMarketplaceId,
    isAccountAgnosticId,
} from "../../utils/permission.utils";
import { logInsightBrowseResults } from "../../utils/telemetry.utils";
import {
    getInsightIdFromSearchParams,
    isMarketingContentFromSearchParams,
} from "../../utils/window.utils";

import {
    DEFAULT_PAGE_SIZE,
    useGetUnifiedContentsAsync,
} from "./useUnifiedContentsAction";
import { useGetUnifiedContentsFiltersV2Async } from "./useUnifiedContentsFiltersActionV2";

export interface UnifiedContentsLoaderProps {
    isLoaderInView?: boolean;
}

/**
 * This loader was added as part of the unified search experience project, and is intended to be the sole content loader for
 * all content types that fall under the scope of that search. As of 4/9/2025, this only supports Packaged Insights due to
 * the incremental rollout of unified search, and as such certain values are hard-coded for this use-case until more progress
 * is made in other systems to support dynamic values.
 */
export function useUnifiedContentsLoader({
    isLoaderInView,
}: UnifiedContentsLoaderProps): [AsyncStatus, Card[], number] {
    const [contents, setContents] = useState<Card[]>([]);
    const [contentCount, setContentCount] = useState<number>(0);
    const [shouldAutoSearch, setShouldAutoSearch] = useState(false);
    const pageNumber = useRef(1);

    const [uatPackagedInsights] = useStore(FeatureFlagsStore, (state) => [
        state.uatPackagedInsights,
    ]);

    const [
        unifiedFiltersV2,
        searchQueryV2,
        filterContentStateIsDirty,
        filterMetadataStateIsDirty,
        setEnabledTypes,
        setUnifiedFilters,
        setIsContentDirty,
        setIsMetadataDirty,
        setLastSearchParameters,
        setLastSearchResultTotalCount,
    ] = useStore(unifiedFilterStore, (state) => [
        state.filters,
        state.searchQuery,
        state.isContentDirty,
        state.isMetadataDirty,
        state.setEnabledTypes,
        state.setFilters,
        state.setIsContentDirty,
        state.setIsMetadataDirty,
        state.setLastSearchParameters,
        state.setLastSearchResultTotalCount,
    ]);

    const searchId = useRef<string>(uuid.v4());
    const searchErrors = useRef<string[]>([]);
    const [upsertNotification, dismissNotification] = useStore(
        notificationStore,
        (state) => [state.upsertNotification, state.dismissNotification],
    );

    const searchAdvertiserId = isAccountAgnosticId()
        ? undefined
        : getAdvertiserId()!;
    const marketplaceId = getMarketplaceId()!;

    const [getContentsState, getContentsAction] = useGetUnifiedContentsAsync({
        advertiserId: searchAdvertiserId,
        marketplaceId,
    });

    const [, getFiltersActionV2] = useGetUnifiedContentsFiltersV2Async({
        advertiserId: searchAdvertiserId,
        marketplaceId,
    });

    const [marketingContentStatus, setMarketingContentStatus] =
        useState<AsyncStatus>("not-executed");

    const insightIdentifier = getInsightIdFromSearchParams();
    const enableSingleIdentifierView = !!insightIdentifier;

    // Determine if we are viewing marketing content in single identifier view
    const isMarketingContentInSingleView =
        enableSingleIdentifierView && isMarketingContentFromSearchParams();

    const contentRequestID = useRef(uuid.v4());
    /**
     * Performs all of the logic needed to retrieve the insights from the the api
     */
    const fetchContents = useCallback(
        async (isPagination = false) => {
            const newId = uuid.v4();
            contentRequestID.current = newId;

            // Until we can implement the event-drive approach for packaged insights (ETA Q3'25), we need a seperate flow for
            // retrieving UAT packaged insights. This is some gross hard-coded logic to force the temporary experience.
            if (uatPackagedInsights) {
                getContentsState.status = "not-executed";
                setContents([]);

                // Stop the scroll from causing another reload
                pageNumber.current = 999;

                const { data: templates = [] } = await (
                    await getPublisherPortalApiClient()
                )
                    .getTemplatesWithAssociations({})
                    .catch((e) => {
                        searchErrors.current = [
                            ...searchErrors.current,
                            e.message,
                        ];
                        return { data: [] };
                    });

                const selectedThemes = unifiedFiltersV2.UNIFIED_TYPE.filter(
                    (item) => item.isSelected,
                );
                const asCard = templates
                    // Filter based on selected types
                    .filter(
                        (t) =>
                            selectedThemes.length === 0 ||
                            selectedThemes.some((s) =>
                                t.program?.themes
                                    ?.map((t) => t.toLowerCase())
                                    .includes(s.label.toLowerCase()),
                            ),
                    )
                    // Filter based on search Query
                    .filter(
                        (t) =>
                            searchQueryV2.trim().length === 0 ||
                            t.name
                                ?.toLowerCase()
                                .includes(searchQueryV2.toLowerCase()),
                    )
                    // Convert to the unified card layout so it can render in the unified viewer
                    .map((t) => {
                        return {
                            identifier: t.id,
                            insightType: InsightType.CDB_TEMPLATE,
                            details: {
                                domainId: t.domain?.id,
                            },
                            header: t.program?.themes?.join(","),
                            title: t.name,
                            subtitle: t.program?.name,
                            footer: t.language,
                            description: t.description,
                            cardHeader: t.name,
                            additionalInfo: {
                                topic: t.program?.themes?.join(","),
                                language: t.language,
                            },
                            lastModifiedDate: t.domain?.releaseTimestamp,
                        } as Card;
                    });

                // We only render the content count if the status is success, so force it here
                getContentsState.status = "success";
                setContentCount(asCard.length);
                setContents(asCard);
                return;
            }

            let curInsights = contents;
            if (!isPagination) {
                setContents([]);
                curInsights = [];
                pageNumber.current = 1;
            }

            // Set last search before API response. If we error out, times out, etc we still want to indicate that
            // we tried a search with the selected parameters already
            setLastSearchParameters();
            await getContentsAction
                .execute(pageNumber.current)
                .then(({ content, totalHits, errors }) => {
                    if (contentRequestID.current !== newId) return;

                    // Push our errors to the error list
                    const additionalErrors = errors.map(
                        (e) => `[${e.provider}]: ${e.errorMessage}`,
                    );
                    searchErrors.current = [
                        ...searchErrors.current,
                        ...additionalErrors,
                    ];

                    setContentCount(totalHits);
                    setLastSearchResultTotalCount(totalHits);

                    setContents([...curInsights, ...content]);
                    pageNumber.current = pageNumber.current + 1;
                })
                .catch((e) => {
                    searchErrors.current = [...searchErrors.current, e.message];
                });
        },
        [searchQueryV2, unifiedFiltersV2, pageNumber, contents],
    );

    // This just needs to be anything that doesn't appear in an insight id
    const filterDelim = "|||";
    const sortAndCleanFilters = (filters: BaseFilterOptionModel[]) => {
        return (
            filters
                .filter(
                    (val, i, arr) =>
                        i === arr.findIndex((val2) => val2.label === val.label),
                )
                // We currently have options being returned with the same id but different labels, which causes the
                // multi-selects to select both options since their ids match. For now we can just overwrite the id with the label
                // But long-term we need to fix the underlying data source from the providers
                .map((val) => {
                    const newId = val.id.includes(filterDelim)
                        ? val.id
                        : `${val.id}${filterDelim}${val.label}`;
                    return { ...val, id: newId };
                })
                .sort((a, b) => a.label.localeCompare(b.label))
        );
    };

    const filterRequestID = useRef(uuid.v4());
    const fetchFiltersV2 = useCallback(async () => {
        // Until we can implement the event-drive approach for packaged insights (ETA Q3'25), we need a seperate flow for
        // retrieving UAT packaged insights. This is some gross hard-coded logic to force the temporary experience.
        if (uatPackagedInsights) {
            // Only enable the UAT types
            const uatTypes = unifiedFiltersV2[CoreUnifiedFilterKeys.TYPE]
                .filter(
                    (t) =>
                        t.enablingFeatureFlag ===
                        FeatureFlags.UAT_PACKAGED_INSIGHT,
                )
                .map((t) => t.id);
            setEnabledTypes(uatTypes);

            // Remove any filter pills options
            Object.values(UnifiedFilterKeys).forEach((key) =>
                setUnifiedFilters(key, []),
            );
            return;
        }

        const newId = uuid.v4();
        filterRequestID.current = newId;

        const hasSelectedValues: Set<UnifiedFilterKeys> = new Set();
        const noSelectedValues: Set<UnifiedFilterKeys> = new Set();

        const selectedOptionsMap: Record<string, any[]> = {};

        // Sort our filters into selected vs not-selected
        Object.entries(unifiedFiltersV2)
            .filter(([key, _]) =>
                Object.values(UnifiedFilterKeys).includes(
                    key as UnifiedFilterKeys,
                ),
            )
            .forEach(([key, value]) => {
                const selectedOptions =
                    (
                        value as { isSelected: boolean; isDisabled: boolean }[]
                    )?.filter(
                        (model) => model.isSelected && !model.isDisabled,
                    ) ?? [];

                const hasSelected = selectedOptions.length > 0;
                if (hasSelected) {
                    hasSelectedValues.add(key as UnifiedFilterKeys);
                    selectedOptionsMap[key] = selectedOptions;
                } else {
                    noSelectedValues.add(key as UnifiedFilterKeys);
                }
            });

        // Get the filters for dropdowns that aren't selected
        const notSelectedPromise = getFiltersActionV2
            .execute({})
            .then(({ filters }) => {
                if (filterRequestID.current !== newId) return;
                noSelectedValues.forEach((key) => {
                    const sorted = sortAndCleanFilters(filters[key] ?? []);

                    // Preselect languages based on selected SF marketplace
                    if (key === UnifiedFilterKeys.LANGUAGE) {
                        const preselectedLanguages = getPreselectedLanguages();
                        const preselectLabels = preselectedLanguages.map(
                            (lang) => lang.label,
                        );
                        let hasPreselections = false;

                        sorted.forEach((option) => {
                            if (preselectLabels.includes(option.label)) {
                                option.isSelected = true;
                                hasPreselections = true;
                            }
                        });

                        setUnifiedFilters(key as UnifiedFilterKeys, sorted);

                        if (hasPreselections) {
                            setLastSearchParameters();
                            setShouldAutoSearch(true);
                        }
                    } else {
                        setUnifiedFilters(key as UnifiedFilterKeys, sorted);
                    }
                });
            })
            .catch((e) => {
                searchErrors.current = [...searchErrors.current, e.message];
            });
        if (filterRequestID.current !== newId) return;

        // Get filters for the dropdowns that are selected. We have to request these individually, pretending no options are selected
        // so that we can see all potential options to add in
        const selectedPromises = Array.from(hasSelectedValues).map((key) => {
            getFiltersActionV2
                .execute({ ignoreField: key })
                .then(({ filters }) => {
                    if (filterRequestID.current !== newId) return;
                    const selected = selectedOptionsMap[key] ?? [];

                    // Force add in the selected values, and then remove any duplicates that caused
                    const newFilters = sortAndCleanFilters([
                        ...selected,
                        ...(filters[key] ?? []),
                    ]);
                    setUnifiedFilters(key as UnifiedFilterKeys, newFilters);
                })
                .catch((e) => {
                    searchErrors.current = [...searchErrors.current, e.message];
                });
        });

        await Promise.all([notSelectedPromise, ...selectedPromises]);
    }, [searchQueryV2, unifiedFiltersV2]);

    /**
     * Update the error message shown to the user when we get a new error
     */
    useEffect(() => {
        if (searchErrors.current.length) {
            upsertNotification(
                <ContentSearchErrorNotificationMessage
                    errors={searchErrors.current}
                />,
                NotificationType.ERROR,
                searchId.current,
            );
        } else {
            dismissNotification(searchId.current);
            searchId.current = uuid.v4();
        }
    }, [searchErrors.current]);

    /**
     * If we have an insight indentifier in the URL, then load just that singular insight
     */
    useEffect(() => {
        if (insightIdentifier) {
            pageNumber.current = 1;

            if (isMarketingContentInSingleView) {
                // Use Discovery API for marketing content in single identiefier view - call directly to get full response
                void (async () => {
                    const response = await (await getDiscoveryApiClient())
                        .getContentDetails(
                            insightIdentifier,
                            ContentPreviewType.ORIGINAL,
                            true,
                        )
                        .catch((e) => {
                            const errorMessage =
                                e instanceof Error ? e.message : String(e);
                            searchErrors.current = [
                                ...searchErrors.current,
                                errorMessage,
                            ];
                            setMarketingContentStatus("error");
                            return null;
                        });

                    if (response) {
                        const unifiedContent: Card[] = response
                            ? [response as Card]
                            : [];

                        setContents(unifiedContent);
                        setContentCount(unifiedContent.length);
                        setMarketingContentStatus("success");
                    }
                })();
            } else {
                // Use existing Cerebro flow for all other content
                void getContentsAction
                    .execute(pageNumber.current, insightIdentifier)
                    .then(({ content, totalHits, errors }) => {
                        // Push our errors to the error list
                        const additionalErrors = errors.map(
                            (e) => `[${e.provider}]: ${e.errorMessage}`,
                        );
                        searchErrors.current = [
                            ...searchErrors.current,
                            ...additionalErrors,
                        ];

                        setContents(content);
                        setContentCount(totalHits);
                    })
                    .catch((e) => {
                        searchErrors.current = [
                            ...searchErrors.current,
                            e.message,
                        ];
                    });
            }
        }
    }, [insightIdentifier, isMarketingContentInSingleView]);

    /**
     * Trigger a refresh of the data when the user scrolls to the bottom of the page, max 1 per second
     */
    useDebouncedEffect(
        () => {
            if (
                isLoaderInView &&
                !isLoading(getContentsState.status) &&
                pageNumber.current <=
                    Math.ceil(contentCount / DEFAULT_PAGE_SIZE) &&
                !enableSingleIdentifierView
            ) {
                searchErrors.current = [];
                void fetchContents(true);
            }
        },
        [
            isLoaderInView,
            contentCount,
            pageNumber,
            DEFAULT_PAGE_SIZE,
            enableSingleIdentifierView,
        ],
        500,
    );

    useEffect(() => {
        if (filterMetadataStateIsDirty) {
            searchErrors.current = [];
            void fetchFiltersV2().then(() => setIsMetadataDirty(false));
        }
    }, [filterMetadataStateIsDirty]);

    useEffect(() => {
        if (shouldAutoSearch && !enableSingleIdentifierView) {
            void fetchContents(false).then(() => setIsContentDirty(false));
            setShouldAutoSearch(false);
        }
    }, [shouldAutoSearch, enableSingleIdentifierView]);

    // When we load this component, dirty our filter state
    useEffect(() => {
        setIsContentDirty(true);
        setIsMetadataDirty(true);
    }, []);

    /**
     * Trigger a refresh of the insights after 500ms of inactivity when:
     * 1. searchQuery - The user has changed the term they're searching for
     * 2. fieldSelections - The user has changed the filters they want to apply
     * 3. contentSubType - The user selected a new insight type
     */
    useDebouncedEffect(
        () => {
            const hasChanges = filterContentStateIsDirty;
            if (!enableSingleIdentifierView && hasChanges) {
                searchErrors.current = [];
                void fetchContents(false).then(() => setIsContentDirty(false));

                setIsMetadataDirty(true);
            }
        },
        [filterContentStateIsDirty, enableSingleIdentifierView],
        200,
    );

    /**
     * Log data each time we retrieve insights
     */
    useDebouncedEffect(
        () => {
            if (isSuccess(getContentsState.status)) {
                logInsightBrowseResults(contents.length);
            }
        },
        [contents, getContentsState.status],
        200,
    );

    if (enableSingleIdentifierView) {
        // Use marketing content state for marketing content in single identifier view, otherwise use Cerebro state
        const stateToUse = isMarketingContentInSingleView
            ? marketingContentStatus
            : getContentsState.status;
        return [stateToUse, contents, contentCount];
    } else {
        return [getContentsState.status, contents, contentCount];
    }
}
